import { memo, useCallback } from "react";
import type { HTMLAttributes, MouseEvent, ReactNode, Ref } from "react";
import {
    FinancialDocument,
    FinancialDocumentExtended,
} from "@common/types/financialDocument";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ImportedStatementIcon, MatchedIcon, ReconciledIcon } from "@icons";
import {
    DocumentFlowStatus,
    getDocumentStatusIconColorClass,
    getImportStatus,
    getImportStatusLabel,
    getMatchStatus,
    getMatchStatusLabel,
    getReconciliationStatus,
    getReconciliationStatusLabel,
    hasTransactionMatches,
} from "./lib";
import styles from "./DocumentStatusIcons.module.scss";

export interface DocumentStatusIconCallbacks {
    onImportClick?: (status: NonNullable<DocumentFlowStatus>) => void;
    onReconciliationClick?: (status: NonNullable<DocumentFlowStatus>) => void;
    onMatchClick?: (status: NonNullable<DocumentFlowStatus>) => void;
}

interface Props extends DocumentStatusIconCallbacks {
    document: FinancialDocument | FinancialDocumentExtended;
}

interface StatusIconButtonProps {
    readonly onClick?: (e: MouseEvent) => void;
    readonly ariaLabel: string;
    readonly children: ReactNode;
}

const StatusIconButton = ({
    onClick,
    ariaLabel,
    children,
    ref,
    ...rest
}: StatusIconButtonProps &
    HTMLAttributes<HTMLButtonElement> & {
        ref?: Ref<HTMLButtonElement>;
    }) => (
    <button
        ref={ref}
        type="button"
        className={onClick ? styles.iconButton : styles.iconWrapper}
        onClick={onClick}
        aria-label={ariaLabel}
        {...rest}
    >
        {children}
    </button>
);

export const DocumentStatusIcons = memo(
    ({
        document,
        onImportClick,
        onReconciliationClick,
        onMatchClick,
    }: Props) => {
        const importStatus = getImportStatus(document);
        const reconciliationStatus = getReconciliationStatus(document);
        const matchStatus = hasTransactionMatches(document)
            ? getMatchStatus(document)
            : null;

        const handleImportClick = useCallback(
            (e: MouseEvent) => {
                e.stopPropagation();
                if (importStatus) {
                    onImportClick?.(importStatus);
                }
            },
            [importStatus, onImportClick],
        );

        const handleReconciliationClick = useCallback(
            (e: MouseEvent) => {
                e.stopPropagation();
                if (reconciliationStatus) {
                    onReconciliationClick?.(reconciliationStatus);
                }
            },
            [reconciliationStatus, onReconciliationClick],
        );

        const handleMatchClick = useCallback(
            (e: MouseEvent) => {
                e.stopPropagation();
                if (matchStatus) {
                    onMatchClick?.(matchStatus);
                }
            },
            [matchStatus, onMatchClick],
        );

        if (!importStatus && !reconciliationStatus && !matchStatus) {
            return null;
        }

        return (
            <div className={styles.container}>
                {importStatus && (
                    <Tooltip content={getImportStatusLabel(importStatus)}>
                        <span>
                            <StatusIconButton
                                onClick={
                                    onImportClick
                                        ? handleImportClick
                                        : undefined
                                }
                                ariaLabel="View import status"
                            >
                                <ImportedStatementIcon
                                    className={getDocumentStatusIconColorClass(
                                        importStatus,
                                    )}
                                />
                            </StatusIconButton>
                        </span>
                    </Tooltip>
                )}

                {reconciliationStatus && (
                    <Tooltip
                        content={getReconciliationStatusLabel(
                            reconciliationStatus,
                        )}
                    >
                        <span>
                            <StatusIconButton
                                onClick={
                                    onReconciliationClick
                                        ? handleReconciliationClick
                                        : undefined
                                }
                                ariaLabel="View reconciliation"
                            >
                                <ReconciledIcon
                                    className={getDocumentStatusIconColorClass(
                                        reconciliationStatus,
                                    )}
                                />
                            </StatusIconButton>
                        </span>
                    </Tooltip>
                )}

                {matchStatus && (
                    <Tooltip content={getMatchStatusLabel(matchStatus)}>
                        <span>
                            <StatusIconButton
                                onClick={
                                    onMatchClick ? handleMatchClick : undefined
                                }
                                ariaLabel="View matched transaction"
                            >
                                <MatchedIcon
                                    className={getDocumentStatusIconColorClass(
                                        matchStatus,
                                    )}
                                />
                            </StatusIconButton>
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    },
);
DocumentStatusIcons.displayName = "DocumentStatusIcons";
